import { useEffect } from "react";
import { formatToman, toPersianDigits } from "../utils/format";

interface FactorUser {
  id?: number;
  name?: string | null;
  family_name?: string | null;
  username?: string | null;
  email?: string | null;
  phone?: string | null;
  person_type?: string;
  company_name?: string | null;
  national_code?: string | null;
  national_id?: string | null;
  postal_code?: string | null;
}

export interface AdminFactor {
  id?: number;
  gateway?: string;
  authority?: string | null;
  amount?: string | number;
  tax?: string | number;
  status?: string;
  status_code?: string | null;
  ref_id?: string | null;
  card_pan?: string | null;
  card_hash?: string | null;
  fee?: string | number | null;
  fee_type?: string | null;
  terminal_number?: string | null;
  acceptor_code?: string | null;
  needs_receipt?: boolean;
  transaction_type?: string;
  plan_name?: string | null;
  duration?: number;
  description?: string | null;
  created_at?: string;
  verified_at?: string | null;
  user?: FactorUser;
}

interface AdminFactorDrawerProps {
  factor: AdminFactor | null;
  onClose: () => void;
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const date = d.toLocaleDateString("fa-IR", { timeZone: "Asia/Tehran", year: "numeric", month: "long", day: "numeric" });
  const time = d.toLocaleTimeString("fa-IR", { timeZone: "Asia/Tehran", hour: "2-digit", minute: "2-digit" });
  return `${date}، ${time}`;
}

function statusBadge(status?: string) {
  switch (status) {
    case "verified":
      return <span className="admin-badge admin-badge-success">موفق</span>;
    case "pending":
      return <span className="admin-badge admin-badge-warning">در انتظار</span>;
    case "failed":
      return <span className="admin-badge admin-badge-error">ناموفق</span>;
    case "expired":
      return <span className="admin-badge admin-badge-warning">منقضی شده</span>;
    default:
      return <span className="admin-badge admin-badge-info">{status || "—"}</span>;
  }
}

function Row({ label, value, ltr = false }: { label: string; value: React.ReactNode; ltr?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>{label}</span>
      <span
        className="text-sm font-medium text-right"
        style={{ color: "var(--admin-text-primary)" }}
        dir={ltr ? "ltr" : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="admin-card overflow-hidden">
      <div className="admin-card-header">
        <h4 className="admin-card-title">{title}</h4>
      </div>
      <div className="p-4 divide-y" style={{ borderColor: "var(--admin-border-light)" }}>
        {children}
      </div>
    </div>
  );
}

export default function AdminFactorDrawer({ factor, onClose }: AdminFactorDrawerProps) {
  useEffect(() => {
    if (!factor) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [factor]);

  if (!factor) return null;

  const user = factor.user as FactorUser | undefined;
  const isLegal = user?.person_type === "legal";
  const fullName = [user?.name, user?.family_name].filter(Boolean).join(" ") || "";
  const amount = Number(factor.amount) || 0;
  const tax = Number(factor.tax) || 0;
  const credited = amount - tax;

  const hasReceiptCodes = Boolean(factor.terminal_number || factor.acceptor_code);

  return (
    <div className="admin-overlay" role="dialog" aria-modal="true">
      {/* Click backdrop to close */}
      <div
        className="absolute inset-0 animate-admin-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel — slides in from the left up to the middle of the page */}
      <aside className="admin-drawer-panel animate-drawer-left absolute top-0 bottom-0 left-0 w-full md:w-1/2 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: "var(--admin-border)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div>
              <h3 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>فاکتور پرداخت</h3>
              <code dir="ltr" className="font-mono text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                #{factor.id ?? "—"}
              </code>
            </div>
            {statusBadge(factor.status)}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-colors hover:opacity-70 shrink-0"
            style={{ color: "var(--admin-text-tertiary)" }}
            aria-label="بستن"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* User info */}
          <Section title="اطلاعات خریدار">
            <Row label="نوع هویت" value={isLegal ? "حقوقی" : "حقیقی"} />
            {isLegal ? (
              <>
                <Row label="نام شرکت" value={user?.company_name || "—"} />
                <Row label="نام کاربری" value={user?.username || "—"} ltr={!user?.username} />
                <Row label="ایمیل" value={user?.email || "—"} ltr={!user?.email} />
                <Row label="شماره موبایل" value={user?.phone ? toPersianDigits(user.phone) : "—"} />
                <Row label="کد اقتصادی" value={user?.national_id ? toPersianDigits(user.national_id) : "—"} ltr={Boolean(user?.national_id)} />
                <Row label="کد پستی" value={user?.postal_code ? toPersianDigits(user.postal_code) : "—"} ltr={Boolean(user?.postal_code)} />
              </>
            ) : (
              <>
                <Row label="نام و نام خانوادگی" value={fullName || "—"} />
                <Row label="نام کاربری" value={user?.username || "—"} ltr={!user?.username} />
                <Row label="ایمیل" value={user?.email || "—"} ltr={!user?.email} />
                <Row label="شماره موبایل" value={user?.phone ? toPersianDigits(user.phone) : "—"} />
                <Row label="کد ملی" value={user?.national_code ? toPersianDigits(user.national_code) : "—"} ltr={Boolean(user?.national_code)} />
                <Row label="کد پستی" value={user?.postal_code ? toPersianDigits(user.postal_code) : "—"} ltr={Boolean(user?.postal_code)} />
              </>
            )}
          </Section>

          {/* Factor info */}
          <Section title="اطلاعات فاکتور">
            <Row label="تاریخ ایجاد" value={formatDateTime(factor.created_at)} />
            <Row label="وضعیت" value={statusBadge(factor.status)} />
            {factor.transaction_type ? (
              <Row
                label="نوع تراکنش"
                value={factor.transaction_type === "subscription_purchase"
                  ? `خرید اشتراک${factor.plan_name ? ` (${factor.plan_name})` : ""}`
                  : factor.transaction_type === "charge_wallet" ? "شارژ کیف پول" : factor.transaction_type}
              />
            ) : null}
          </Section>

          {/* Payment details */}
          <Section title="جزئیات پرداخت">
            <Row label="مبلغ کل" value={`${formatToman(factor.amount)} تومان`} />
            <Row label="مالیات" value={`${formatToman(factor.tax)} تومان`} />
            <Row label="مبلغ واریز شده" value={`${formatToman(credited)} تومان`} />
            {hasReceiptCodes ? (
              <>
                {factor.terminal_number ? (
                  <Row label="شماره پایانه" value={toPersianDigits(factor.terminal_number)} ltr />
                ) : null}
                {factor.acceptor_code ? (
                  <Row label="کد پذیرنده" value={toPersianDigits(factor.acceptor_code)} ltr />
                ) : null}
              </>
            ) : null}
            <Row label="شناسه ارجاع" value={factor.ref_id ? toPersianDigits(factor.ref_id) : "—"} ltr={Boolean(factor.ref_id)} />
            <Row label="شناسه پرداخت" value={factor.authority ? toPersianDigits(factor.authority) : "—"} ltr={Boolean(factor.authority)} />
            {factor.card_pan ? <Row label="شماره کارت" value={toPersianDigits(factor.card_pan)} ltr /> : null}
            {factor.card_hash ? <Row label="هش کارت" value={toPersianDigits(factor.card_hash)} ltr /> : null}
            {factor.fee != null ? <Row label="کارمزد درگاه" value={`${formatToman(factor.fee)} تومان`} /> : null}
            {factor.fee_type ? <Row label="پرداختکننده کارمزد" value={factor.fee_type === "Payer" ? "خریدار" : factor.fee_type === "Merchant" ? "پذیرنده" : factor.fee_type} /> : null}
            <Row label="تاریخ تکمیل" value={formatDateTime(factor.verified_at)} />
          </Section>
        </div>
      </aside>
    </div>
  );
}